import type { MutableRefObject } from "react";

import { scribeLogger } from "../logger";
import { scribeStore } from "../stores/scribe";
import {
  transcriptStore,
  useAddTranscriptItem,
  useTranscriptItemsList,
} from "../stores/transcript";
import type { ClientNoteTranscriptItem, NablaTranscriptItem } from "../types";
import type { PromiseOrchestrator } from "../utils/async/PromiseOrchestrator";
import { createTranscriptItemFromNablaData } from "../utils/nabla";
import type { SaveTranscriptItem } from "./scribeSessionTypes";
import useSyncedRef from "./useSyncedRef";

export type UseTranscriptItemsOptions = {
  saveTranscriptItem: SaveTranscriptItem;
  lastOffset: number;
};

/**
 * Maintains the list of transcript items associated with the current recording
 * and provides some utilities for working with the list.
 */
export function useTranscriptItems(
  recordingOrchestratorRef: MutableRefObject<PromiseOrchestrator>,
  options: UseTranscriptItemsOptions,
): {
  saveNonFinalizedTranscriptItems: () => void;
  processTranscriptItem: (data: NablaTranscriptItem) => Promise<void>;
  transcriptItems: ClientNoteTranscriptItem[];
} {
  const { saveTranscriptItem, lastOffset } = options;

  const transcriptItems = useTranscriptItemsList();

  const addTranscriptItem = useAddTranscriptItem();

  const saveTranscriptItemRef = useSyncedRef(saveTranscriptItem);

  const processTranscriptItem = async (
    data: NablaTranscriptItem,
  ): Promise<void> => {
    const sessionId = scribeStore.getState().scribingSessionId;

    if (!sessionId) {
      throw new Error(
        "Unable to process transcript item: missing scribing session ID",
      );
    }

    const transcriptItem = createTranscriptItemFromNablaData(data, lastOffset);

    addTranscriptItem(transcriptItem);

    if (data.is_final) {
      const savePromise = saveTranscriptItemRef.current({
        scribingSessionId: sessionId,
        data: transcriptItem,
      });

      recordingOrchestratorRef.current.add(savePromise);

      await savePromise;
    }
  };

  /**
   * Takes all non-finalized items in the client-side transcript item map, marks
   * them final, and saves them to our backend. This is specifically for the scenario
   * where our scribe client dies but we have non-finalized transcript items that,
   * due to the client dying, will never see their finalized version from the BE. In
   * those situations, it's better to at least persist the non-finalized items and
   * accept that they might not be as accurate as a finalized item would be, instead of
   * completely losing those items from the transcript.
   */
  const saveNonFinalizedTranscriptItems = (): void => {
    const transcriptStoreState = transcriptStore.getState();

    const nonFinalizedItems =
      transcriptStoreState.getNonFinalizedTranscriptItems();

    const sessionId = scribeStore.getState().scribingSessionId;

    if (nonFinalizedItems.length === 0) return;

    scribeLogger?.info("Finalizing orphaned transcript items", {
      itemCount: nonFinalizedItems.length,
      totalDuration: nonFinalizedItems.reduce((accum, transcriptItem) => {
        const duration =
          transcriptItem.end_offset_ms - transcriptItem.start_offset_ms;
        return accum + duration;
      }, 0),
    });

    if (!sessionId) {
      throw new Error(
        "Unable to emergency save non-finalized transcript items: missing scribing session ID",
      );
    }

    transcriptStore.getState().markTranscriptItemsFinal(nonFinalizedItems);

    nonFinalizedItems.forEach((item) => {
      const savePromise = saveTranscriptItemRef.current({
        scribingSessionId: sessionId,
        data: item,
      });
      recordingOrchestratorRef.current.add(savePromise);
    });
  };

  return {
    processTranscriptItem,
    saveNonFinalizedTranscriptItems,
    transcriptItems,
  };
}
